import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import * as pdfjsLib from "pdfjs-dist/webpack";
import EntityConstants from "../configs/EntityConstants";
import { isWasmQrScannerEnabled } from "../configs/featureFlags";
import { GET_PUBLIC } from "../service/CRUDService";
import {
  decodeQrData,
  dedupeQrPayloads,
  scanFilesForAllQr,
  scanFilesWithURLForQr,
} from "../utils/qr-utils";
import {
  resolveEncodedDataFromVc,
} from "../utils/vc-context";
import { isCredentialSubjectEmpty } from "../utils/vcVerificationDisplay";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const readFileAsUint8Array = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

const convertPdfFirstPageToImage = async (pdfFile, dpi = 300) => {
  const pdfDataU8 = await readFileAsUint8Array(pdfFile);
  const pdf = await pdfjsLib.getDocument({ data: pdfDataU8 }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: dpi / 72 });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;
  return new Promise((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", 0.95);
  });
};

const convertToFullPageImage = async (file) => {
  if (file.type === "application/pdf") {
    return convertPdfFirstPageToImage(file);
  }
  return new Blob([file], { type: file.type });
};

const normalizeScanPayloads = (scanResult) => {
  const data = scanResult?.data;
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

const scanUploadedFileForQr = async (file) => {
  const collected = [];

  const urlScanResult = await scanFilesWithURLForQr(file);
  if (urlScanResult?.data) {
    collected.push(urlScanResult.data);
  }

  const zxingScanResult = await scanFilesForAllQr(file);
  if (zxingScanResult?.data?.length) {
    collected.push(...zxingScanResult.data);
  }

  let unique = dedupeQrPayloads(collected);
  if (unique.length > 0) {
    return { scanResult: { data: unique }, cropperSource: null };
  }

  let fullPageBlob = null;

  if (file.type === "application/pdf") {
    const pdfDataU8 = await readFileAsUint8Array(file);
    const pdf = await pdfjsLib.getDocument({ data: pdfDataU8 }).promise;
    const maxPages = Math.min(pdf.numPages, 20);
    for (let i = 1; i <= maxPages; i += 1) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 300 / 72 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;
      const pageBlob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      const pageFile = new File([pageBlob], `page-${i}.png`, {
        type: "image/png",
      });
      const pageScanResult = await scanFilesForAllQr(pageFile);
      if (pageScanResult?.data?.length) {
        collected.push(...pageScanResult.data);
        // WASM / STP scans every page for multi-QR PDFs. Legacy Ooru
        // stops after the first page that contains a QR.
        if (!isWasmQrScannerEnabled()) break;
      }
    }
  } else {
    fullPageBlob = await convertToFullPageImage(file);
    const fullPageFile = new File([fullPageBlob], "full-page.jpg", {
      type: "image/jpeg",
    });
    const fullPageScan = await scanFilesForAllQr(fullPageFile);
    if (fullPageScan?.data?.length) {
      collected.push(...fullPageScan.data);
    }
  }

  unique = dedupeQrPayloads(collected);
  if (unique.length > 0) {
    return { scanResult: { data: unique }, cropperSource: null };
  }

  const cropperSource =
    file.type === "application/pdf"
      ? await fileToDataUrl(file)
      : await fileToDataUrl(
          fullPageBlob
            ? new File([fullPageBlob], "full-page.jpg", {
                type: "image/jpeg",
              })
            : file,
        );

  return { scanResult: { data: [] }, cropperSource };
};

const extractEncodedDataFromUrl = (data) => {
  if (typeof data !== "string") return null;

  const urlPathRegex = /\/verify\/([a-f0-9]{64})$/i;
  const urlParamsRegex = /[?&]encoded_data=([^&]+)/;

  let match = data.match(urlPathRegex);
  if (match) return match[1];

  match = data.match(urlParamsRegex);
  if (match) return decodeURIComponent(match[1]);

  try {
    const url = new URL(data);
    const encodedData = url.searchParams.get("encoded_data");
    if (encodedData) return encodedData;

    const pathMatch = url.pathname.match(/\/verify\/([a-f0-9]{64})$/i);
    return pathMatch?.[1] || null;
  } catch {
    return null;
  }
};

const isOurCredentialEncodedData = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^https?:\/\//i.test(trimmed)) return false;
  if (trimmed.startsWith("did:")) return true;
  if (trimmed.startsWith("credissuer:")) return true;
  if (/^[a-f0-9]{64}$/i.test(trimmed)) return true;
  return false;
};

/**
 * decodeQrData returns a string. For VC QR payloads that string is JSON —
 * same as Validate.jsx decodeVcData: JSON.parse → resolve encoded_data for
 * the verify API. Cryptographic verification is done by the backend.
 */
const credentialIdFromVcPayload = async (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) return null;

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  return resolveEncodedDataFromVc(parsed);
};

const extractCredentialId = async (qrData) => {
  if (!qrData) return null;

  const rawValue =
    typeof qrData === "object" && qrData !== null ? qrData.data : qrData;
  if (!rawValue) return null;

  if (typeof rawValue === "string") {
    const fromUrl = extractEncodedDataFromUrl(rawValue);
    if (isOurCredentialEncodedData(fromUrl)) return fromUrl;

    if (/^https?:\/\//i.test(rawValue.trim())) {
      return null;
    }

    if (isOurCredentialEncodedData(rawValue)) {
      return rawValue.trim();
    }

    const fromRawVc = await credentialIdFromVcPayload(rawValue);
    if (isOurCredentialEncodedData(fromRawVc)) return fromRawVc;
  }

  let decoded = null;
  try {
    decoded = await decodeQrData(qrData);
  } catch (error) {
    console.error("QR decode failed:", error);
  }

  if (typeof decoded === "string") {
    const fromDecodedUrl = extractEncodedDataFromUrl(decoded);
    if (isOurCredentialEncodedData(fromDecodedUrl)) return fromDecodedUrl;

    if (/^https?:\/\//i.test(decoded.trim())) {
      return null;
    }

    const fromDecodedVc = await credentialIdFromVcPayload(decoded);
    if (isOurCredentialEncodedData(fromDecodedVc)) return fromDecodedVc;

    if (isOurCredentialEncodedData(decoded)) return decoded.trim();
    return null;
  }

  if (decoded && typeof decoded === "object") {
    const credentialId = await resolveEncodedDataFromVc(decoded);
    if (!isOurCredentialEncodedData(credentialId)) return null;
    return credentialId;
  }

  return null;
};

const collectCredentialIdsFromPayloads = async (payloads) => {
  const ids = [];
  const seen = new Set();

  for (const payload of payloads) {
    try {
      const credentialId = await extractCredentialId({ data: payload });
      if (!isOurCredentialEncodedData(credentialId) || seen.has(credentialId)) {
        continue;
      }
      seen.add(credentialId);
      ids.push(credentialId);
    } catch (error) {
      console.warn("Skipping QR that could not yield credential id:", error);
    }
  }

  return ids;
};

const fetchVerificationDetails = async (credentialId, captchaToken) => {
  const captchaQuery = captchaToken
    ? `&captchaToken=${encodeURIComponent(captchaToken)}`
    : "";
  const response = await GET_PUBLIC(
    `${EntityConstants.VERIFY_CREDENTIAL_NO_AUTH}${encodeURIComponent(
      credentialId,
    )}${captchaQuery}`,
  );
  return response.json();
};

const fetchVerificationDetailsBatch = async (credentialIds, captchaToken) => {
  const encodedData = credentialIds.map(encodeURIComponent).join(",");
  const captchaQuery = captchaToken
    ? `&captchaToken=${encodeURIComponent(captchaToken)}`
    : "";
  const response = await GET_PUBLIC(
    `${EntityConstants.VERIFY_CREDENTIAL_NO_AUTH}${encodedData}${captchaQuery}`,
  );
  return response.json();
};

export const normalizeBatchVerificationResponse = (payload, fallbackCount) => {
  let entries = [];
  if (Array.isArray(payload?.data)) {
    entries = payload.data;
  } else if (payload?.data && typeof payload.data === "object") {
    entries = [payload.data];
  } else if (Array.isArray(payload)) {
    entries = payload;
  } else if (payload) {
    entries = [payload];
  }

  const count =
    typeof payload?.count === "number" ? payload.count : fallbackCount;

  const successes = [];
  const errors = [];

  for (const entry of entries) {
    if (!entry) continue;
    if (entry.error_code) {
      errors.push(entry);
      continue;
    }
    if (entry.error && !entry.credential_subject) {
      errors.push(entry);
      continue;
    }
    if (isCredentialSubjectEmpty(entry?.credential_subject)) {
      continue;
    }
    successes.push(entry);
  }

  return { count, successes, errors };
};

const usePublicVerification = () => {
  const { t } = useTranslation(["publicPortal", "verify"]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState(null);

  const resetResult = useCallback(() => setResult(null), []);

  const verifyCredential = useCallback(
    async ({ credentialId, captchaToken }) => {
      if (!credentialId) {
        throw new Error(t("verification.invalidCredential"));
      }

      setIsVerifying(true);
      setResult(null);

      try {
        const data = await fetchVerificationDetails(credentialId, captchaToken);
        setResult(data);
        return data;
      } finally {
        setIsVerifying(false);
      }
    },
    [t],
  );

  const verifyCredentials = useCallback(
    async ({ credentialIds, captchaToken }) => {
      const ids = (credentialIds || []).filter(Boolean);
      if (ids.length === 0) {
        throw new Error(t("verification.invalidCredential"));
      }

      if (ids.length === 1) {
        const data = await verifyCredential({
          credentialId: ids[0],
          captchaToken,
        });
        const normalized = normalizeBatchVerificationResponse(data, 1);
        return normalized;
      }

      setIsVerifying(true);
      setResult(null);

      try {
        const payload = await fetchVerificationDetailsBatch(ids, captchaToken);
        const normalized = normalizeBatchVerificationResponse(
          payload,
          ids.length,
        );
        setResult(
          normalized.successes.length === 1
            ? normalized.successes[0]
            : normalized.successes,
        );
        return normalized;
      } finally {
        setIsVerifying(false);
      }
    },
    [t, verifyCredential],
  );

  const verifyScan = useCallback(
    async (detectedCodes, captchaToken = null) => {
      const rawValue = detectedCodes?.[0]?.rawValue;
      const credentialId = await extractCredentialId(rawValue);
      return verifyCredential({ credentialId, captchaToken });
    },
    [t, verifyCredential],
  );

  const prepareFile = useCallback(
    async (file, captchaToken) => {
      const supportedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      const minSize = 5 * 1024;
      const maxSize = 15 * 1024 * 1024;

      if (!supportedTypes.includes(file.type)) {
        throw new Error(t("verification.invalidFileFormat"));
      }
      if (file.size < minSize || file.size > maxSize) {
        throw new Error(t("verification.invalidFileSize"));
      }

      const document = await fileToDataUrl(file);
      const { scanResult, cropperSource } = await scanUploadedFileForQr(file);
      const payloads = normalizeScanPayloads(scanResult);

      // Cropper is only for failed detection — same as Validate.jsx.
      if (payloads.length === 0) {
        return {
          needsCrop: true,
          captchaToken,
          cropperImageSrc: cropperSource || document,
          document,
          fileName: file.name,
          fileType: file.type,
        };
      }

      const credentialIds = await collectCredentialIdsFromPayloads(payloads);
      if (credentialIds.length === 0) {
        throw new Error(t("verification.invalidCredential"));
      }

      return {
        needsCrop: false,
        captchaToken,
        credentialIds,
        credentialId: credentialIds[0],
        document,
        fileName: file.name,
        fileType: file.type,
      };
    },
    [t],
  );

  const prepareCroppedFile = useCallback(
    async (croppedBlob, pendingMeta) => {
      const croppedFile = new File([croppedBlob], "cropped-qr-area.jpg", {
        type: "image/jpeg",
      });

      const collected = [];
      const urlScanResult = await scanFilesWithURLForQr(croppedFile);
      if (urlScanResult?.data) {
        collected.push(urlScanResult.data);
      }
      const zxingScanResult = await scanFilesForAllQr(croppedFile);
      if (zxingScanResult?.data?.length) {
        collected.push(...zxingScanResult.data);
      }

      const payloads = dedupeQrPayloads(collected);
      if (payloads.length === 0) {
        throw new Error(t("verify:unable_to_scan_selected_area"));
      }

      const credentialIds = await collectCredentialIdsFromPayloads(payloads);
      if (credentialIds.length === 0) {
        throw new Error(t("verification.invalidCredential"));
      }

      return {
        needsCrop: false,
        captchaToken: pendingMeta?.captchaToken || null,
        credentialIds,
        credentialId: credentialIds[0],
        document: pendingMeta?.document,
        fileName: pendingMeta?.fileName,
        fileType: pendingMeta?.fileType,
      };
    },
    [t],
  );

  const verifyFile = useCallback(
    async (file, captchaToken) => {
      const preparedFile = await prepareFile(file, captchaToken);
      if (preparedFile.needsCrop) {
        throw new Error(t("verification.qrNotFound"));
      }
      return verifyCredentials(preparedFile);
    },
    [prepareFile, t, verifyCredentials],
  );

  return {
    isVerifying,
    prepareCroppedFile,
    prepareFile,
    resetResult,
    result,
    verifyCredential,
    verifyCredentials,
    verifyFile,
    verifyScan,
  };
};

export default usePublicVerification;
